import { useState, FC, useEffect } from "react";

import { useTheme } from "@mui/material";
import {
  postTranslateAllNameDescriptionSurveyFields,
  TranslateAllSurveysResponse,
  listOrg,
} from "@rubi/api-generated";
import { colors } from "@rubi/ui";

import { DevPage, ErrorMsg, Section } from "./common";
import "./dev.scss";

interface Organization {
  id: string;
  title: string;
  short_title?: string | null;
}

export const DevTranslateAllNameDescriptionValues: FC = () => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<TranslateAllSurveysResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [fetchingOrgs, setFetchingOrgs] = useState(true);

  // Fetch organizations on component mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setFetchingOrgs(true);
        const response = await listOrg();
        if (response.data) {
          setOrganizations(response.data);
        }
      } catch (err) {
        console.error("Error fetching organizations:", err);
        setError("Failed to fetch organizations");
      } finally {
        setFetchingOrgs(false);
      }
    };

    fetchOrganizations();
  }, []);

  const handleTranslateAll = async () => {
    if (!selectedOrgId) {
      setError("Please select an organization");
      return;
    }
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await postTranslateAllNameDescriptionSurveyFields({
        body: { rubi_org_id: selectedOrgId },
        throwOnError: false,
      });

      if (response.status === 200) {
        setResult(response.data!);
      } else {
        setError(
          `Error ${response.status}: ${response.error?.console_msg || "Unknown error"}`,
        );
      }
    } catch (err) {
      setError(
        `Unexpected error: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DevPage title="Translate All Survey Name & Description Values">
      <Section subtitle="Translation Action">
        <p>
          This tool will translate all survey names and descriptions to multiple
          languages using Azure Translator. This process may take several
          minutes depending on the number of surveys.
        </p>

        <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
          <label
            htmlFor="organization-select"
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
            }}
          >
            Select Organization:
          </label>
          <select
            id="organization-select"
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            disabled={fetchingOrgs}
            style={{
              width: "100%",
              padding: "0.5rem",
              fontSize: "1rem",
              border: "1px solid #ccc",
              borderRadius: "0.375rem",
              backgroundColor: fetchingOrgs
                ? "var(--action-hover)"
                : "var(--background-paper)",
            }}
          >
            <option value="">
              {fetchingOrgs
                ? "Loading organizations..."
                : "Select an organization"}
            </option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.title} {org.short_title && `(${org.short_title})`}
              </option>
            ))}
          </select>
        </div>

        {selectedOrgId && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.75rem",
              backgroundColor: "var(--action-hover)",
              borderRadius: "0.375rem",
              border: "1px solid var(--divider)",
            }}
          >
            <strong>Selected:</strong>{" "}
            {organizations.find((org) => org.id === selectedOrgId)?.title}
            {organizations.find((org) => org.id === selectedOrgId)
              ?.short_title &&
              ` (${organizations.find((org) => org.id === selectedOrgId)?.short_title})`}
          </div>
        )}

        <div style={{ marginTop: "1rem" }}>
          <button
            onClick={handleTranslateAll}
            disabled={isLoading || !selectedOrgId || fetchingOrgs}
            className="btn btn-primary"
            style={{
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              backgroundColor:
                isLoading || !selectedOrgId || fetchingOrgs
                  ? colors.action.active
                  : colors.primary.main,
              border: "none",
              borderRadius: "0.375rem",
              color: colors.common.white,
              cursor:
                isLoading || !selectedOrgId || fetchingOrgs
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {isLoading ? "Translating..." : "Translate All Surveys"}
          </button>
        </div>
      </Section>

      {error && (
        <Section subtitle="Error">
          <ErrorMsg msg={error} />
        </Section>
      )}

      {result && (
        <Section subtitle="Translation Results">
          <div
            style={{
              backgroundColor: "var(--action-hover)",
              padding: "1rem",
              borderRadius: "0.375rem",
              border: "1px solid var(--divider)",
            }}
          >
            <h4>Summary</h4>
            <ul style={{ marginBottom: "1rem" }}>
              <li>
                <strong>Total Surveys:</strong> {result.total_surveys}
              </li>
              <li>
                <strong>Successful Translations:</strong>{" "}
                {result.successful_translations}
              </li>
              <li>
                <strong>Failed Translations:</strong>{" "}
                {result.failed_translations}
              </li>
            </ul>

            {result.errors.length > 0 && (
              <div>
                <h4>Errors ({result.errors.length})</h4>
                <ul
                  style={{
                    backgroundColor: "var(--divider)",
                    padding: "0.75rem",
                    borderRadius: "0.25rem",
                    border: "1px solid var(--divider)",
                    color: "var(--text-primary)",
                  }}
                >
                  {result.errors.map((error, index) => (
                    <li key={index} style={{ marginBottom: "0.25rem" }}>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.successful_translations === result.total_surveys && (
              <div
                style={{
                  backgroundColor: "var(--action-selected)",
                  padding: "0.75rem",
                  borderRadius: "0.25rem",
                  border: "1px solid var(--divider)",
                  // success.dark is unreadable on a dark surface.
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.success.light
                      : colors.success.dark,
                  marginTop: "1rem",
                }}
              >
                ✅ All surveys translated successfully!
              </div>
            )}
          </div>
        </Section>
      )}
    </DevPage>
  );
};
